import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TitleCasePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';

import { ListToMap } from '../../../../lib/lib-ngx/utils/ListToMap';
import { OrderAddressType } from '../../../../lib/lib-shared/types/OrderAddressType';
import { ConfirmationModalComponent } from '../../modals/confirmation-modal/confirmation-modal.component';
import { ApiLavandierService } from '../../../../lib/lib-ngx/web-services/api-lavandier.service';
import {
  UserProfileSendMessageModalComponent
} from '../../modals/user-profile-send-message-modal/user-profile-send-message-modal.component';
import { ErrorService } from '../../../../lib/lib-ngx/services/error.service';
import { UserDataVerificationModalComponent } from '../../modals/user-data-verification-modal/user-data-verification-modal.component';
import { AddPaymentModalComponent } from '../../modals/add-payment-modal/add-payment-modal.component';
import { ErrorMessage } from '../../../../lib/lib-shared/ErrorMessage';
import { PaymentStatus } from '../../../../lib/lib-shared/types/PaymentStatus';
import { OrderTemporary } from '../../../../lib/lib-shared/types/OrderTemporary';
import { OrderArticleTrackingDefectStationMap } from '../../../../lib/lib-ngx/web-services/TypeMap/OrderArticleTrackingDefectStation';
import { OrderArticleTrackingDefectTypeMap } from '../../../../lib/lib-ngx/web-services/TypeMap/OrderArticleTrackingDefectType';
import { ImagePreviewModalComponent } from '../../modals/image-preview-modal/image-preview-modal.component';
import { OrderStatusStatus } from '../../../../lib/lib-shared/types/OrderStatusStatus';
import { OrderType } from '../../../../lib/lib-shared/types/OrderType';
import { PRICEMULTIPLIER } from '../../../../lib/lib-shared/defines';
import { ITransportSheet } from '../../../../lib/lib-shared/models/TransportSheet';
import { IOrderDetails } from '../../../../lib/lib-shared/models/Order';
import { TransportSheetType } from '../../../../lib/lib-shared/types/TransportSheetType';
import { IHouseDetailsList } from '../../../../lib/lib-shared/models/House';
import { IDiscountCode } from '../../../../lib/lib-shared/models/DiscountCode';

@Component({
  selector: 'lm-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {
  public PRICEMULTIPLIER = PRICEMULTIPLIER;
  public OrderAddressType = OrderAddressType;
  public OrderStatusStatus = OrderStatusStatus;
  public TransportSheetType = TransportSheetType;
  public OrderTemporary = OrderTemporary;
  public OrderArticleTrackingDefectStationMap = OrderArticleTrackingDefectStationMap;
  public OrderArticleTrackingDefectTypeMap = OrderArticleTrackingDefectTypeMap;

  @ViewChild('articleTable') articleTable: any;
  @ViewChild('articleByBagTable') articleByBagTable: any;
  public orderId = null;
  public order: IOrderDetails = null;
  public discountCode: IDiscountCode | null = null;

  public lavandierMap = new Map();
  public addCredit = false;
  public userTypeMap = new Map();
  public storageMap = new Map();
  public houseMap: Map<number, IHouseDetailsList> = new Map();

  get canCancelOrder(): boolean {
    const isKit = [OrderType.KIT, OrderType.KIT_BATCH].includes(this.order.type);
    return !this.order.cancel
      && ((isKit && this.order.status === OrderStatusStatus.CREATE) || this.order.type === OrderType.PRE_ORDER);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiLavandierService: ApiLavandierService,
    private modalService: NgbModal,
    private errorService: ErrorService,
    private router: Router,
    public sanitizer: DomSanitizer,
  ) {
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(
      (params: any) => {
        this.orderId = params.id;
        this.loadData();
      }
    );
  }

  loadData() {
    return forkJoin([
      this.loadOrderWithDiscount(),
      this.apiLavandierService.getLavandiers(),
      this.apiLavandierService.getUserTypeList({}),
      this.apiLavandierService.getStorageList({}),
    ])
      .pipe(
        map(([orderData, lavandierList, userTypeList, storageList]) => ({
          order: orderData.order,
          discountCode: orderData.discountCode,
          lavandierMap: ListToMap.convert(lavandierList.rows),
          userTypeMap: ListToMap.convert(userTypeList as any[]),
          storageMap: ListToMap.convert((storageList as any[])),
        })),
      )
      .subscribe((({order, discountCode, lavandierMap, userTypeMap, storageMap}) => {
        this.order = order;
        this.discountCode = discountCode;
        this.lavandierMap = lavandierMap as any;
        this.userTypeMap = userTypeMap as any;
        this.storageMap = storageMap as any;
        this.loadUserHouseList();
      }));
  }

  private loadOrderWithDiscount() {
    return this.apiLavandierService.getOrderIdDetails(this.orderId, {}).pipe(
      switchMap(order => order.discountCodeId
        ? this.apiLavandierService.getDiscountCode(order.discountCodeId, {}).pipe(
          map(discountCode => ({order: order, discountCode: discountCode})),
        )
        : of({order: order, discountCode: null})
      )
    );
  }

  getTransportSheetList() {
    const transportSheetMap: Map<number, ITransportSheet> = new Map();

    for (const orderBag of this.order.orderBags) {
      transportSheetMap.set(orderBag.transportSheet.id, orderBag.transportSheet);
    }

    for (const orderShipReturn of this.order.orderShipReturnList) {
      for (const trackingSheet_TransportSheet of orderShipReturn.trackingSheet_TransportSheetList) {
        if (!transportSheetMap.has(trackingSheet_TransportSheet.transportSheet.id)) {
          transportSheetMap.set(trackingSheet_TransportSheet.transportSheet.id, trackingSheet_TransportSheet.transportSheet);
        }
      }
    }

    return Array.from(transportSheetMap.values());
  }

  getOrderArticleList() {
    const orderArticleList = [];
    this.order.orderBags.forEach(orderBag => {
      orderBag.orderArticles.map((orderArticle: any) => {
        const bag = Object.assign({}, orderBag);
        delete bag.orderArticles;
        orderArticle.orderBag = bag;
      });
      orderArticleList.push(...orderBag.orderArticles);
    });
    return orderArticleList;
  }

  onAddPayment() {
    if (this.order.paymentList.some(payment => payment.status === PaymentStatus.PROCESSING)) {
      return this.errorService.showModal(ErrorMessage.ORDER_PAYMENT_PROCESSING);
    }
    const addPaymentModalComponent = this.modalService.open(AddPaymentModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    addPaymentModalComponent.result
      .then((payment) => this.apiLavandierService.postOrderIdPay(this.order.id, payment)
        .subscribe(data => {
          if (!this.errorService.manageError(data)) {
            this.ngOnInit();
          }
        }))
      .catch(() => null);
  }

  getLastAddressByType(type: OrderAddressType) {
    const orderAddressListOfType = this.order.orderAddresses.filter(orderAddress => orderAddress.type === type);
    return orderAddressListOfType[orderAddressListOfType.length - 1];
  }

  loadUserHouseList() {
    this.apiLavandierService.getHouseUserId(this.order.userId, {})
      .subscribe((houseList) => {
        this.houseMap = ListToMap.convert(houseList);
      });
  }

  loadInvoice() {
    this.apiLavandierService.getDocInvoice(this.order.id)
      .subscribe(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            this.errorService.manageError(JSON.parse(reader.result));
          } catch (e) {
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.target = '_blank';
            a.download = 'facture-commande-' + this.order.id;
            a.click();
            window.URL.revokeObjectURL(a.href);
          }
        };
        reader.readAsText(blob);
      });
  }

  openModal() {
    const modal = this.modalService.open(UserProfileSendMessageModalComponent, {size: 'lg'});
    modal.componentInstance.user = this.order.user;
  }

  getOrderArticleTrackingDefectList(): any[] {
    const orderArticleTrackingDefectList = [];

    for (const orderBag of this.order.orderBags) {
      for (const orderArticle of orderBag.orderArticles) {
        for (const orderArticleTracking of orderArticle.orderArticleTrackingList) {
          for (const orderArticleTrackingDefect of orderArticleTracking.orderArticleTrackingDefectList) {
            orderArticleTrackingDefectList.push({
              ...orderArticleTrackingDefect, ...{
                parentId: orderArticleTrackingDefect.parentId ? orderArticleTrackingDefect.parentId : orderArticleTrackingDefect.id,
                orderArticleTracking: {...orderArticleTracking, ...{orderArticle: orderArticle}}
              }
            });
          }
        }
      }
    }

    return orderArticleTrackingDefectList;
  }

  openImagePreviewModal(event) {
    if (event.type === 'click') {
      event.cellElement.blur();
      const orderArticleTrackingDefect = event.row;
      const imagePreviewModal = this.modalService.open(ImagePreviewModalComponent, {size: 'lg'});
      imagePreviewModal.componentInstance.imageList = [orderArticleTrackingDefect];
      imagePreviewModal.componentInstance.selectedImage = orderArticleTrackingDefect;
    }
  }

  onCloseDefect(event: Event, defectId: number, closedSuccess: boolean) {
    event.stopPropagation();
    const confirmModal = this.modalService.open(ConfirmationModalComponent);
    confirmModal.componentInstance.confirmationDesc = `Êtes-vous sûr de vouloir clôturer ce défaut ${closedSuccess ? 'avec succès' : 'en échec'} ?`;
    confirmModal.componentInstance.validateButtonText = 'Confirmer';
    confirmModal.componentInstance.cancelButtonText = 'Annuler';
    confirmModal.result.then(confirmed => {
      if (confirmed) {
        this.apiLavandierService.putOrderArticleTrackingDefectIdClose(defectId, {closedSuccess: closedSuccess})
          .subscribe(() => this.loadData());
      }
    }).catch(() => null);
  }

  onManuallyFinish() {
    this.apiLavandierService.putOrderIdFinish(this.orderId).subscribe(data => {
      if (!this.errorService.manageError(data)) {
        this.ngOnInit();
      }
    });
  }

  onCancel() {
    const modal = this.modalService.open(ConfirmationModalComponent, {size: 'lg'});
    modal.componentInstance.confirmationDesc = 'Êtes-vous sûr de vouloir annuler cette commande ?';
    modal.componentInstance.validateButtonText = 'Confirmer';
    modal.componentInstance.cancelButtonText = 'Annuler';
    modal.result
      .then((confirm: boolean) => {
        if (confirm) {
          this.apiLavandierService.putOrderCancel(this.order.id, {})
            .subscribe(data => {
              if (!this.errorService.manageError(data)) {
                this.ngOnInit();
              }
            });
        }
      })
      .catch(() => null);
  }
}
